import { AnimatePresence, motion } from "framer-motion";
import { CheckIcon, CopyIcon, LinkIcon } from "lucide-react";
import { useCallback, useState } from "react";

import { Button } from "@/components/ui/button";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { cn } from "@/lib/utils";

export const UrlBar = ({ url }: { url: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [url]);

  return (
    <div className="flex min-w-0 items-center gap-2 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/50">
      <LinkIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
      <OverflowDisplayTooltip content={url} side="bottom">
        <span className="min-w-0 flex-1 truncate font-mono text-xs text-gray-600 dark:text-gray-400">
          {url}
        </span>
      </OverflowDisplayTooltip>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleCopy}
        className={cn(
          "h-6 shrink-0 gap-1 rounded-md px-2 text-xs font-medium transition-all duration-200",
          copied
            ? "border-transparent bg-emerald-50 text-emerald-600 hover:bg-emerald-50 dark:bg-emerald-500/10 dark:text-emerald-400 dark:hover:bg-emerald-500/10"
            : "active:scale-[0.97]",
        )}
      >
        <AnimatePresence mode="wait" initial={false}>
          {copied ? (
            <motion.span
              key="check"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="flex items-center gap-1"
            >
              <CheckIcon className="h-3 w-3" />
              Copied
            </motion.span>
          ) : (
            <motion.span
              key="copy"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="flex items-center gap-1"
            >
              <CopyIcon className="h-3 w-3" />
              Copy
            </motion.span>
          )}
        </AnimatePresence>
      </Button>
    </div>
  );
};
